import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload as UploadIcon, 
  X, 
  CheckCircle2, 
  XCircle, 
  Clock,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { api } from '@/utils/api';
import { useWebSocket } from '@/hooks/useWebSocket';

interface UploadFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  error?: string;
  fileId?: string;
}

const SUPPORTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/tiff',
  'text/plain',
  'text/html',
  'application/rtf',
];

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const Upload: React.FC = () => {
  const [files, setFiles] = useState<UploadFile[]>([]);

  // WebSocket for real-time status updates
  useWebSocket({
    onFileStatusUpdate: (update) => {
      setFiles((prev) =>
        prev.map((f) => {
          if (f.fileId === update.fileId) {
            if (update.status === 'COMPLETED') {
              return { ...f, status: 'completed' };
            } else if (update.status === 'FAILED' || update.status === 'TIMEOUT') {
              return { ...f, status: 'error', error: update.errorMessage || 'Processing failed' };
            } else if (['PENDING', 'QUEUED', 'PROCESSING'].includes(update.status)) {
              return { ...f, status: 'processing' };
            }
          }
          return f;
        })
      );
    },
  });

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: unknown[]) => {
    if (rejectedFiles.length > 0) {
      console.warn('Rejected files:', rejectedFiles);
    }

    const newFiles: UploadFile[] = acceptedFiles.map((file) => ({
      id: Math.random().toString(36).substring(7),
      file,
      status: 'pending',
      progress: 0,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: SUPPORTED_TYPES.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    maxSize: MAX_FILE_SIZE,
  });

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const uploadFile = async (file: UploadFile) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === file.id ? { ...f, status: 'uploading' } : f))
    );

    try {
      // Create upload
      const { fileId, uploadUrl } = await api.createUpload(
        file.file.name,
        file.file.type,
        file.file.size
      );

      setFiles((prev) =>
        prev.map((f) => (f.id === file.id ? { ...f, fileId } : f))
      );

      // Upload to S3
      await api.uploadToS3(uploadUrl, file.file, (progress) => {
        setFiles((prev) =>
          prev.map((f) => (f.id === file.id ? { ...f, progress } : f))
        );
      });

      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, status: 'processing', progress: 100 } : f
        )
      );
    } catch (error) {
      console.error('Upload failed:', error);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id
            ? { ...f, status: 'error', error: 'Upload failed' }
            : f
        )
      );
    }
  };


  const uploadAll = () => {
    files
      .filter((f) => f.status === 'pending')
      .forEach((file) => uploadFile(file));
  };

  const getStatusIcon = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <Clock className="h-5 w-5 text-blue-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Completed</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      case 'processing':
        return <Badge variant="secondary">Processing</Badge>;
      case 'uploading':
        return <Badge variant="secondary">Uploading</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Upload Files</h1>
        <p className="text-muted-foreground mt-1">
          Securely upload and sanitize your documents
        </p>
      </div>

      {/* Drop Zone */}
      <Card>
        <CardContent className="pt-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            <UploadIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            {isDragActive ? (
              <p className="text-lg font-medium">Drop files here...</p>
            ) : (
              <>
                <p className="text-lg font-medium mb-2">
                  Drag & drop files here, or click to select
                </p>
                <p className="text-sm text-muted-foreground">
                  Supported: PDF, Word, Excel, PowerPoint, Images (Max 100MB)
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* File List */}
      {files.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Files to Upload</CardTitle>
                <CardDescription>
                  {files.length} file{files.length !== 1 ? 's' : ''} selected
                </CardDescription>
              </div>
              <Button
                onClick={uploadAll}
                disabled={!files.some((f) => f.status === 'pending')}
              >
                <UploadIcon className="mr-2 h-4 w-4" />
                Upload All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-4 p-4 border rounded-lg"
              >
                <FileText className="h-8 w-8 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{file.file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(file.file.size)}
                  </p>
                  {file.status === 'uploading' && (
                    <Progress value={file.progress} className="mt-2 h-2" />
                  )}
                  {file.error && (
                    <p className="text-sm text-destructive mt-1">{file.error}</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {getStatusIcon(file.status)}
                  {getStatusBadge(file.status)}
                  {file.status === 'pending' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => uploadFile(file)}
                    >
                      <UploadIcon className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFile(file.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-muted/50">
        <CardContent className="flex items-start gap-4 pt-6">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
          <div>
            <p className="font-medium">Security Notice</p>
            <p className="text-sm text-muted-foreground">
              All files are processed through our secure sanitization pipeline. 
              Executable code, scripts, and metadata are removed while preserving 
              the visual content. Files are automatically deleted after 7 days.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Upload;
